import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Commission } from '../types/commission';
import { ReviewPanel } from './ReviewPanel';
interface FlaggedCommissionsPanelProps {
  commissions: Commission[];
  onClose: () => void;
  onSubmitCounter: (commissionId: string, loanId: string, message: string) => void;
  onSubmitResolution: (commissionId: string, overrides: {
    loanId: string;
    newCommission: number;
  }[], message: string) => void;
}
export function FlaggedCommissionsPanel({
  commissions,
  onClose,
  onSubmitCounter,
  onSubmitResolution
}: FlaggedCommissionsPanelProps) {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const flaggedCommissions = commissions.filter(c => ['flagged', 'under_review', 'resolved_pending_approval'].includes(c.review?.status || '') || c.loans.some(l => ['flagged', 'under_review'].includes(l.approvalStatus)));
  // Always look up the latest commission data from props
  const selectedCommission = selectedUserId ? commissions.find(c => c.userId === selectedUserId) || null : null;
  return ReactDOM.createPortal(<>
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center" onClick={onClose} data-id="element-410">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} onClick={e => e.stopPropagation()} className="bg-white w-full max-w-xl max-h-[85vh] shadow-2xl overflow-hidden flex flex-col rounded-2xl" data-id="element-411">
          {/* Header */}
          <div className="p-6 border-b border-slate-200 flex-shrink-0" data-id="element-412">
            <div className="flex items-center justify-between" data-id="element-413">
              <div data-id="element-414">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-415">
                  Flagged Commissions
                </h2>
                <p className="text-sm text-slate-600 mt-1" data-id="element-416">
                  {flaggedCommissions.length} commission
                  {flaggedCommissions.length !== 1 ? 's' : ''} pending review
                </p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-417">
                <X size={24} className="text-slate-500" data-id="element-418" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto p-6" data-id="element-419">
            {flaggedCommissions.length > 0 ? <div className="space-y-3" data-id="element-420">
                {flaggedCommissions.map(commission => {
              const flaggedCount = commission.loans.filter(l => ['flagged', 'under_review'].includes(l.approvalStatus)).length;
              return <motion.button key={commission.userId} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} onClick={() => setSelectedUserId(commission.userId)} className="w-full bg-white border-2 border-amber-200 rounded-xl p-4 hover:border-amber-300 hover:shadow-md transition-all text-left group" data-id="element-421">
                      <div className="flex items-center justify-between mb-3" data-id="element-422">
                        <div className="flex items-center gap-3" data-id="element-423">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm" data-id="element-424">
                            {commission.userName.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div data-id="element-425">
                            <p className="text-sm font-bold text-slate-900 group-hover:text-amber-700 transition-colors" data-id="element-426">
                              {commission.userName}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-427">
                              {commission.userProfile}
                            </p>
                          </div>
                        </div>
                        <ChevronRight size={20} className="text-slate-400 group-hover:text-amber-600 transition-colors" data-id="element-428" />
                      </div>
                      <div className="flex items-center justify-between" data-id="element-429">
                        <div data-id="element-430">
                          <p className="text-xs text-slate-500 mb-1" data-id="element-431">Period</p>
                          <p className="text-sm font-semibold text-slate-900" data-id="element-432">
                            {commission.payPeriod}
                          </p>
                        </div>
                        <div className="text-right" data-id="element-433">
                          <p className="text-xs text-slate-500 mb-1" data-id="element-434">Status</p>
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${commission.review?.status === 'under_review' ? 'bg-blue-100 text-blue-700' : commission.review?.status === 'resolved_pending_approval' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-435">
                            {commission.review?.status === 'under_review' ? 'Under Review' : commission.review?.status === 'resolved_pending_approval' ? 'Resolved' : `${flaggedCount} Flagged`}
                          </span>
                        </div>
                      </div>
                    </motion.button>;
            })}
              </div> : <div className="text-center py-12" data-id="element-436">
                <p className="text-slate-500" data-id="element-437">No flagged commissions</p>
              </div>}
          </div>
        </motion.div>
      </motion.div>

      {/* Review Modal */}
      <AnimatePresence data-id="element-438">
        {selectedCommission && <ReviewPanel commission={selectedCommission} onClose={() => setSelectedUserId(null)} onSubmitCounter={onSubmitCounter} onSubmitResolution={onSubmitResolution} data-id="element-439" />}
      </AnimatePresence>
    </>, document.body);
}